<template>
  <div>
    <div class="hero-top">
      <div
        class="hero-top-inner"
        :style="{
          'background-image':
            pageData.banner_image && pageData.banner_image.url
              ? 'url(' + imageLocation + pageData.banner_image.url + ')'
              : 'url(' + imageLocation + '/homepage/homepage-banner-1440x730.webp)',
        }"
      >
        <div class="hero-leaf-reverse">
          <h2 v-html="pageData.intro_text_1" />
          <h1 v-html="pageData.intro_text_2 + headerLocationText" />
        </div>
        <ClientOnly>
          <AddressInput buttonText="Get a quote" isHomePageSearch />
        </ClientOnly>
      </div>
    </div>
    <div class="hero-bottom" id="hero_bottom">
      <div class="mx-3 md:mx-12 hero-cards" id="hero-cards">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <NuxtLink
            :to="pageData.section_1_box_1_button_link"
            class="card lawn-care"
          >
            <h2 v-html="pageData.section_1_box_1_title" />
            <p class="shadowed-text" v-html="pageData.section_1_box_1_text" />
            <span class="icon-arrow" />
            <img
              v-if="
                pageData.section_1_box_1_image &&
                pageData.section_1_box_1_image.url
              "
              :src="imageLocation + pageData.section_1_box_1_image.url"
              :alt="pageData.section_1_box_1_image.alt"
              class="card-img"
              loading="lazy"
            />
          </NuxtLink>
          <NuxtLink
            :to="pageData.section_1_box_2_button_link"
            class="card pest-control"
          >
            <h2 v-html="pageData.section_1_box_2_title" />
            <p class="shadowed-text" v-html="pageData.section_1_box_2_text" />
            <span class="icon-arrow" />
            <img
              v-if="
                pageData.section_1_box_2_image &&
                pageData.section_1_box_2_image.url
              "
              :src="imageLocation + pageData.section_1_box_2_image.url"
              :alt="pageData.section_1_box_2_image.alt"
              class="card-img"
              loading="lazy"
            />
          </NuxtLink>
          <NuxtLink
            :to="pageData.section_1_box_3_button_link"
            class="card tree-care"
          >
            <h2 v-html="pageData.section_1_box_3_title" />
            <p class="shadowed-text" v-html="pageData.section_1_box_3_text" />
            <span class="icon-arrow" />
            <img
              v-if="
                pageData.section_1_box_3_image &&
                pageData.section_1_box_3_image.url
              "
              :src="imageLocation + pageData.section_1_box_3_image.url"
              :alt="pageData.section_1_box_3_image.alt"
              class="card-img"
              loading="lazy"
            />
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type State from "~/domains/store/State";

// Props
interface PageData {}

interface Props {
  pageData: PageData;
  location: string;
}

const props = defineProps<Props>();

const { $config } = useNuxtApp();

// Reactive state
const imageLocation = ref($config.public.sgCmsImage);

// Vuex store
const store: Store<State> = useStore();

// Computed properties
const headerLocationText = computed(() => {
  return props.location ? ` in the ${props.location} Area` : "";
});

// Methods
const switchPersistanceState = () => {
  store.dispatch("switchPersistanceState");
};

const resize = () => {
  const heroCards = document.getElementById("hero-cards");
  if (heroCards) {
    const heroCardsHeight = heroCards.offsetHeight;
    const heroBottom = document.getElementById("hero_bottom");
    if (heroBottom) {
      heroBottom.style.height = `${heroCardsHeight}px`;
    }
  }
};

// Lifecycle hooks
onMounted(() => {
  resize();
  window.addEventListener("resize", resize);
});

watch(
  () => props.location,
  () => {
    resize();
  }
);

defineExpose({
  resize,
});
</script>

  
<style scoped>
.hero-top {
  position: relative;
  height: 577px;
  border-bottom-right-radius: 33px;
}

.hero-form {
  width: 600px;
}

.hero-top-inner {
  background-repeat: no-repeat;
  background-position: 50%;
  width: 100%;
  height: calc(100% + 150px);
  position: absolute;
  border-bottom-left-radius: 70px;
  background-size: cover;
}

.hero-top-inner .hero-form {
  top: 60%;
}

.hero-img {
  position: absolute;
  top: 0px;
  left: 0px;
  border-bottom-left-radius: 70px;
  max-width: none;
  height: auto;
}

.hero-bottom {
  position: relative;
  width: 90%;
}

.hero-leaf-reverse {
  position: absolute;
  left: 3rem;
  top: 60px;
  background: linear-gradient(
    90deg,
    #00953a 37.9%,
    rgba(77, 164, 58, 0.48) 121.27%
  );
  background-size: 100% 100%;
  width: 600px;
  padding: 65px 60px 20px 40px;
  max-width: 80%;
  border-top-left-radius: 70px;
  border-bottom-right-radius: 70px;
}

.hero-leaf-reverse h2 {
  color: #fff;
  font-size: 1.2em;
  line-height: 1.2;
}

.hero-leaf-reverse h1 {
  color: #fff;
  line-height: 1.1;
  font-size: 2.25em;
}

.card {
  border-radius: 33px;
  padding: 30px 50% 70px 30px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 40%,
    rgba(238, 241, 244, 1) 100%
  );
  position: relative;
  min-height: 240px;
  z-index: 9;
}

.card span.icon-arrow {
  position: absolute;
  left: 30px;
  bottom: 30px;
}

.hero-cards {
  position: absolute;
  top: 0;
  right: -10%;
}

.hero-cards span.icon-arrow {
  color: #fff;
  background: #4da43a url(~/assets/images/arrow-white.png) no-repeat 55% center;
  border-radius: 21px;
  width: 42px;
  height: 42px;
  display: inline-block;
  text-align: center;
  z-index: 50;
}

.hero-cards .shadowed-text {
  text-shadow: 1px 1px 5px #fcfdfd, 1px 1px 5px #fcfdfd, 1px 1px 10px #fcfdfd,
    1px 1px 10px #fcfdfd;
}

.card-img {
  width: 80%;
  position: absolute;
  right: 0;
  bottom: 0;
  max-height: 100%;
}

@media (max-width: 900px) {
  .hero-top {
    border-bottom-right-radius: 0;
  }

  .hero-bottom {
    width: 100%;
  }

  .hero-cards {
    right: unset;
  }
}

@media (max-width: 640px) {
  .hero-leaf h1 {
    font-size: 1.5em;
  }

  .hero-leaf-reverse {
    left: 2.5rem;
  }

  .hero-form {
    left: 2rem;
  }

  .hero-top-inner {
    background-position: 65%;
  }

  .hero-leaf-reverse h1 {
    font-size: 1.5em;
  }

  .card {
    padding: 30px 90px 70px 30px;
  }

  .hero-cards .card-img {
    opacity: 0.4;
    z-index: -1;
  }
}
</style>